import React, { useState, useMemo, useEffect } from "react";
import { View, ScrollView, TextInput, Pressable, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { supabase } from "@/lib/supabase-client";
import useAuth from "@/hooks/useAuth";

export type TwilioNumberCapabilities = {
  voice: boolean;
  sms: boolean;
  mms: boolean;
  fax: boolean;
};

export type TwilioNumber = {
  phoneNumber: string;
  friendlyName: string | null;
  capabilities: TwilioNumberCapabilities;
  voiceConfigured: boolean;
  smsConfigured: boolean;
};

export type PhonePickerProps = {
  pickMany?: boolean;
  selectedNumbers?: string[];
  onPick: (numbers: string[]) => void;
  onCancel?: () => void;
};

export function PhonePicker({ pickMany = false, selectedNumbers = [], onPick, onCancel }: PhonePickerProps) {
  const { colors } = useTheme();
  const { session } = useAuth();
  const [search, setSearch] = useState("");
  const [numbers, setNumbers] = useState<TwilioNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<string[]>(selectedNumbers);

  // Fetch phone numbers on mount
  useEffect(() => {
    let cancel = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase.functions.invoke("twilio", {
          body: { action: "list_numbers" },
          headers: { Authorization: `Bearer ${session?.access_token}` },
        });

        if (cancel) return;

        if (fetchError) {
          console.error("Failed to fetch phone numbers:", fetchError);
          setError(fetchError.message || "Failed to load phone numbers");
          return;
        }

        // Handle both response formats: { numbers: [...] } or [...]
        const phoneList = Array.isArray(data?.numbers) ? data.numbers : Array.isArray(data) ? data : [];
        setNumbers(phoneList);
      } catch (err) {
        if (!cancel) {
          console.error("Unexpected error fetching phone numbers:", err);
          setError("An unexpected error occurred");
        }
      } finally {
        if (!cancel) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancel = true;
    };
  }, [session?.access_token]);

  const filteredNumbers = useMemo(() => {
    if (!search) return numbers;
    const searchLower = search.toLowerCase();
    return numbers.filter((number) => {
      const friendlyName = number.friendlyName?.toLowerCase() || "";
      const phoneNumber = number.phoneNumber.toLowerCase();
      return friendlyName.includes(searchLower) || phoneNumber.includes(searchLower);
    });
  }, [numbers, search]);

  const handleNumberToggle = (phoneNumber: string) => {
    if (pickMany) {
      setSelectedPhoneNumbers((prev) =>
        prev.includes(phoneNumber) ? prev.filter((num) => num !== phoneNumber) : [...prev, phoneNumber],
      );
    } else {
      onPick([phoneNumber]);
    }
  };

  const handleConfirm = () => {
    onPick(selectedPhoneNumbers);
  };

  const renderNumber = (number: TwilioNumber) => {
    const isSelected = selectedPhoneNumbers.includes(number.phoneNumber);
    const caps = number.capabilities;

    return (
      <Pressable
        key={number.phoneNumber}
        testID={`phone-option-${number.phoneNumber}`}
        style={[
          styles.numberRow,
          { borderBottomColor: colors.border },
          isSelected && pickMany && { backgroundColor: colors.backgroundSecondary },
        ]}
        onPress={() => handleNumberToggle(number.phoneNumber)}
      >
        <View style={styles.numberContent}>
          {/* Friendly Name Column */}
          <View style={styles.nameColumn}>
            <ThemedText type="defaultSemiBold" style={styles.cellText}>
              {number.friendlyName || "Untitled"}
            </ThemedText>
          </View>

          {/* Capability Columns */}
          <View style={styles.capabilityColumn}>
            <ThemedText style={styles.capabilityText}>{caps?.voice ? "✓" : "—"}</ThemedText>
          </View>
          <View style={styles.capabilityColumn}>
            <ThemedText style={styles.capabilityText}>{caps?.sms ? "✓" : "—"}</ThemedText>
          </View>
          <View style={styles.capabilityColumn}>
            <ThemedText style={styles.capabilityText}>{caps?.mms ? "✓" : "—"}</ThemedText>
          </View>
          <View style={styles.capabilityColumn}>
            <ThemedText style={styles.capabilityText}>{caps?.fax ? "✓" : "—"}</ThemedText>
          </View>

          {/* Checkbox for pickMany mode */}
          {pickMany && (
            <View style={styles.checkboxColumn}>
              <MaterialCommunityIcons
                name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"}
                size={24}
                color={isSelected ? colors.primary : colors.text}
              />
            </View>
          )}
        </View>
      </Pressable>
    );
  };

  return (
    <View testID="phone-picker" style={styles.container}>
      <ThemedText type="subtitle">{pickMany ? "Select Phone Numbers" : "Select Phone Number"}</ThemedText>

      <TextInput
        testID="phone-picker-search"
        style={[
          styles.searchInput,
          {
            borderColor: colors.border,
            color: colors.text,
            backgroundColor: colors.backgroundSecondary,
          },
        ]}
        placeholder="Search by name or number..."
        placeholderTextColor={colors.placeholder}
        value={search}
        onChangeText={setSearch}
        autoFocus
        editable={!loading}
      />

      {/* Table Header */}
      {!loading && !error && filteredNumbers.length > 0 && (
        <View
          style={[
            styles.tableHeader,
            { borderBottomColor: colors.border, backgroundColor: colors.backgroundSecondary },
          ]}
        >
          <View style={styles.nameColumn}>
            <ThemedText style={styles.headerText}>Friendly Name</ThemedText>
          </View>
          <View style={styles.capabilityColumn}>
            <ThemedText style={styles.headerText}>📞</ThemedText>
          </View>
          <View style={styles.capabilityColumn}>
            <ThemedText style={styles.headerText}>💬</ThemedText>
          </View>
          <View style={styles.capabilityColumn}>
            <ThemedText style={styles.headerText}>📷</ThemedText>
          </View>
          <View style={styles.capabilityColumn}>
            <ThemedText style={styles.headerText}>📠</ThemedText>
          </View>
          {pickMany && <View style={styles.checkboxColumn} />}
        </View>
      )}

      <ScrollView style={styles.scrollView}>
        {loading ? (
          <ThemedText style={styles.message}>Loading phone numbers...</ThemedText>
        ) : error ? (
          <ThemedText style={[styles.message, { color: colors.red.text }]}>{error}</ThemedText>
        ) : filteredNumbers.length > 0 ? (
          filteredNumbers.map(renderNumber)
        ) : (
          <ThemedText style={styles.message}>
            {search ? "No phone numbers found" : "No phone numbers available"}
          </ThemedText>
        )}
      </ScrollView>

      {/* Footer */}
      {pickMany && (
        <View style={styles.footer}>
          <ThemedText style={styles.selectedCount}>{selectedPhoneNumbers.length} selected</ThemedText>
          <View style={styles.footerButtons}>
            {onCancel && (
              <Button
                testID="phone-picker-cancel"
                text="Cancel"
                style={[styles.cancelButton, { borderColor: colors.border }]}
                textStyle={{ color: colors.text }}
                onPress={onCancel}
              />
            )}
            <Button
              testID="phone-picker-confirm"
              text="Confirm"
              style={styles.confirmButton}
              onPress={handleConfirm}
              enabled={selectedPhoneNumbers.length > 0}
            />
          </View>
        </View>
      )}

      {!pickMany && onCancel && (
        <View style={styles.footer}>
          <Button
            testID="phone-picker-cancel"
            text="Cancel"
            style={[
              styles.cancelButton,
              {
                borderColor: colors.border,
              },
            ]}
            textStyle={{ color: colors.text }}
            onPress={onCancel}
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 8,
    gap: 8,
  },
  searchInput: {
    height: 36,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  tableHeader: {
    flexDirection: "row",
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 2,
    alignItems: "center",
  },
  scrollView: {
    maxHeight: 350,
  },
  numberRow: {
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    backgroundColor: "transparent",
  },
  numberContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  nameColumn: {
    flex: 2,
    minWidth: 200,
    paddingRight: 12,
  },
  capabilityColumn: {
    flex: 0.5,
    minWidth: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  checkboxColumn: {
    width: 40,
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 8,
  },
  headerText: {
    fontSize: 12,
    fontWeight: "700",
    textTransform: "uppercase",
    opacity: 0.8,
  },
  cellText: {
    fontSize: 13,
    lineHeight: 18,
  },
  capabilityText: {
    fontSize: 13,
    lineHeight: 18,
    textAlign: "center",
  },
  message: {
    opacity: 0.7,
    alignSelf: "center",
    marginVertical: 20,
  },
  footer: {
    marginTop: 8,
  },
  selectedCount: {
    opacity: 0.7,
    marginBottom: 8,
  },
  footerButtons: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
  },
  cancelButton: {
    height: 32,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  confirmButton: {
    height: 32,
    paddingHorizontal: 16,
  },
});
